/**
 * Script loader utility for dynamically loading external scripts
 */

interface ScriptConfig {
    src: string;
    id: string;
    type?: string;
  }
  
  const SCRIPT_CONFIGS: Record<string, ScriptConfig> = {
    ima: {
      src: 'https://imasdk.googleapis.com/js/sdkloader/ima3.js',
      id: 'ima-sdk',
      type: 'text/javascript'
    },
    system73: {
      src: '//cdn.s73cloud.com/4/s73-sdk-shakaplayer.js',
      id: 'system73-sdk',
      type: 'application/javascript'
    }
  };
  
  /**
   * Load a script dynamically
   */
  function loadScript(config: ScriptConfig): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if script is already loaded
      if (document.getElementById(config.id)) {
        resolve();
        return;
      }
  
      const script = document.createElement('script');
      script.id = config.id;
      script.src = config.src;
      script.type = config.type || 'text/javascript';
      script.async = true;
  
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load script: ${config.src}`));
  
      document.head.appendChild(script);
    });
  }
  
  /**
   * Get required script loaders based on configuration
   */
  export function getRequiredScriptLoaders(needsIma: boolean, needsSystem73: boolean): Promise<void>[] {
    const loaders: Promise<void>[] = [];
  
    if (needsIma) {
      loaders.push(loadScript(SCRIPT_CONFIGS.ima));
    }
  
    if (needsSystem73) {
      loaders.push(loadScript(SCRIPT_CONFIGS.system73));
    }
  
    return loaders;
  }
  
  /**
   * Load multiple scripts in parallel
   */
  export async function loadScripts(scriptLoaders: Promise<void>[]): Promise<void> {
    if (scriptLoaders.length === 0) {
      return;
    }
  
    try {
      await Promise.all(scriptLoaders);
    } catch (error) {
      console.error('Error loading scripts:', error);
      throw error;
    }
  }
  
  /**
   * Check if a script is already loaded
   */
  export function isScriptLoaded(scriptName: keyof typeof SCRIPT_CONFIGS): boolean {
    const config = SCRIPT_CONFIGS[scriptName];
    return !!document.getElementById(config.id);
  }
  
  /**
   * Wait for a global variable to be available (useful for waiting for SDK initialization)
   */
  export function waitForGlobal(globalName: string, timeout: number = 5000): Promise<any> {
    return new Promise((resolve, reject) => {
      const startTime = Date.now();
  
      function checkGlobal() {
        const global = (window as any)[globalName];
        if (global) {
          resolve(global);
          return;
        }
  
        if (Date.now() - startTime > timeout) {
          reject(new Error(`Timeout waiting for global: ${globalName}`));
          return;
        }
  
        setTimeout(checkGlobal, 100);
      }
  
      checkGlobal();
    });
  }
  
  /**
   * Wait for multiple globals to be available
   */
  export async function waitForGlobals(globalNames: string[], timeout: number = 5000): Promise<void> {
    const promises = globalNames.map(name => waitForGlobal(name, timeout));
    await Promise.all(promises);
  }  